import { Component, EventEmitter, forwardRef, HostListener, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { AutoComplete, AutoCompleteSelectEvent } from 'primeng/autocomplete';

@Component({
  selector: 'app-generic-autocomplete',
  standalone: false,
  templateUrl: './generic-autocomplete.component.html',
  styleUrl: './generic-autocomplete.component.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => GenericAutocompleteComponent),
    multi: true
  }]
})
export class GenericAutocompleteComponent<T> implements ControlValueAccessor {
  
  @Input({ required: true }) items: T[] = [];
  @Input({ required: true }) inputId: string;
  @Input({ required: true }) label: string;
  @Input({ required: true }) displayField: string;
  
  @Output() selectionChange = new EventEmitter<T | null>();
  @Output() blur = new EventEmitter<Event>();
  @Output() focus = new EventEmitter<Event>();
  @Output() keydown = new EventEmitter<KeyboardEvent>();

  @ViewChild(AutoComplete) private autoComplete?: AutoComplete;

  // Faz o PrimeNG usar o próprio objeto da opção como valor (em vez de option.value). O cast é só pela tipagem string do optionValue.
  readonly optionValueIdentity = ((option: T) => option) as unknown as string;

  filtered: T[] = [];
  value: T | null = null;
  disabled = false;

  private onChange: (value: T | null) => void = () => {};
  private onTouched = () => {};

  // Impede que o mousedown numa opção da overlay tire o foco do input (a overlay está no body, por isso o listener é no document).
  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.autoComplete?.overlayVisible) return;

    if ((event.target as HTMLElement | null)?.closest('.p-autocomplete-overlay'))
      event.preventDefault();
  }

  writeValue(value: T | null): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  filter(event: { query: string }) {
    const q = event.query?.toLowerCase() ?? '';

    this.filtered = this.items.filter(item => {
      const value = (item as any)[this.displayField];
      return value?.toString().toLowerCase().includes(q);
    });
  }

  handleSelect(event: AutoCompleteSelectEvent) {
    this.value = event.value;
    this.onChange(event.value);
    this.selectionChange.emit(event.value);
  }

  // O PrimeNG dispara (ngModelChange) e (onSelect) juntos numa seleção — só emitir aqui quando for
  // uma limpeza (valor nulo), senão selectionChange sai duplicado (handleSelect já cobre a seleção).
  handleInputChange(value: T | null) {
    this.value = value;
    this.onChange(value);
    if (!value) this.selectionChange.emit(value);
  }

  handleBlur(event: Event) {
    this.onTouched();
    this.blur.emit(event);
  }

  handleFocus(event: any) {
    this.focus.emit(event);
  }

  handleKeydown(event: KeyboardEvent) {
    this.keydown.emit(event);
  }
}